import {
  getBodyAgeGroup,
  getBodyImage,
  getOrganImages,
  type OrganArtworkPart,
  type OrganArtworkQuality,
} from '#assets/images/gutBodyConnections';
import type {
  GutBodyConnectionsColorVariant,
  GutBodyConnectionsOrgan,
  OrganLookupCode,
} from '@repo/types';
import { AgeGroup, getAgeGroup } from '@repo/utils';

export type GutBodyConnectionsSystem = {
  organ: GutBodyConnectionsOrgan;
  parts: OrganArtworkPart[];
};

/**
 * Figma's head-down reading order. `organs.priority` cannot serve: science sets
 * that by prominence, and the map paints in that order. Post-MVP codes are listed
 * too, so the order is right the day their artwork lands.
 */
const ORGAN_DISPLAY_ORDER: OrganLookupCode[] = [
  'brain',
  'oral',
  'endocrine',
  'immune',
  'skin',
  'lung',
  'heart',
  'gut',
  'liver',
  'metabolic',
  'muscle',
  'reproductive',
  'kidney',
];

/** An unknown code sorts last, where `indexOf`'s -1 would put it first. */
const rank = (lookupCode: OrganLookupCode) => {
  const index = ORGAN_DISPLAY_ORDER.indexOf(lookupCode);
  return index === -1 ? ORGAN_DISPLAY_ORDER.length : index;
};

const byOrganDisplayOrder = (
  a: GutBodyConnectionsSystem,
  b: GutBodyConnectionsSystem,
) => rank(a.organ.lookup_code) - rank(b.organ.lookup_code);

type ResolveGutBodyConnectionsSystemsArgs = {
  organs: GutBodyConnectionsOrgan[];
  /** `kit.age_at_sampling`, in days. */
  age: number;
  pregnant: boolean;
  sex: string;
  quality?: OrganArtworkQuality;
  /** Draw this colour's artwork instead of the flag-level colours. */
  colorVariant?: GutBodyConnectionsColorVariant;
};

/**
 * The single answer to which organs a kit's body can draw, so no surface can name
 * a system the others do not: `getOrganImages` returns nothing for `oral`, for
 * adult-only organs on a younger body, or for a uterus on a male.
 *
 * `systems` keeps the order the organs arrived in, which the map paints in — they
 * are absolutely positioned at one z-index, so DOM order is stacking order and the
 * gut has to stay under the liver. `listedSystems` is the reading order.
 */
export function resolveGutBodyConnectionsSystems({
  organs,
  age,
  pregnant,
  sex,
  quality,
  colorVariant,
}: ResolveGutBodyConnectionsSystemsArgs) {
  const ageGroup = getAgeGroup(age);
  const bodyAgeGroup = getBodyAgeGroup({ age, pregnant });

  const drawnCodes = new Set(organs.map(organ => organ.lookup_code));

  const systems = organs
    .map(organ => ({
      organ,
      parts: getOrganImages({
        ageGroup,
        sex,
        pregnant,
        drawnCodes,
        quality,
        colorVariant,
        flagLevel: organ.flag_level,
        lookupCode: organ.lookup_code,
      }),
    }))
    .filter(system => system.parts.length > 0);

  return {
    body: getBodyImage(bodyAgeGroup),
    bodyAgeGroup,
    drawnCodes,
    systems,
    listedSystems: [...systems].sort(byOrganDisplayOrder),
  };
}

/**
 * The organs the kit's body has artwork for, in the order they arrived. Surfaces
 * that list organs without drawing them filter through this, so a system science
 * ships ahead of its artwork stays hidden, conditions and all.
 */
export const getDrawableOrgans = (
  args: Omit<ResolveGutBodyConnectionsSystemsArgs, 'quality' | 'colorVariant'>,
): GutBodyConnectionsOrgan[] =>
  resolveGutBodyConnectionsSystems(args).systems.map(({ organ }) => organ);

const BABY_BODY_AGE_GROUPS: AgeGroup[] = [AgeGroup.BABY, AgeGroup.TODDLER];

/** The single colour neutral organs are drawn in, whatever their flags. */
export const NEUTRAL_ORGAN_COLOR_VARIANT: GutBodyConnectionsColorVariant =
  'purple';

/**
 * A neutral colour for anything drawn on the baby-toddler body, so a parent never
 * sees their child's organs in flag colours. Undefined keeps the flag colours.
 */
export const getNeutralOrganColorVariant = ({
  age,
  pregnant,
  enabled,
}: {
  /** `kit.age_at_sampling`, in days. */
  age: number;
  pregnant: boolean;
  enabled: boolean;
}): GutBodyConnectionsColorVariant | undefined =>
  enabled && BABY_BODY_AGE_GROUPS.includes(getBodyAgeGroup({ age, pregnant }))
    ? NEUTRAL_ORGAN_COLOR_VARIANT
    : undefined;
